import { applicationUrlFor } from '@repo/contracts';
import { Skeleton } from '@repo/ui';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { APP_ROUTES } from '@/constants/app-routes';
import { appRolesOf } from '@/hooks/auth/app-roles';
import { useMyApplications } from '@/hooks/auth/use-my-applications';
import { useSetActiveRole } from '@/hooks/auth/use-set-active-role';
import { frontDoorTarget } from '@/lib/front-door-target';
import NoAccessPage from '@/pages/login/NoAccessPage';
import { announceSession } from '@/shared/utils/session-channel';
import { selectActiveRole, selectHeldRoles } from '@/store/auth.slice';
import { useAppSelector } from '@/store/store';

/**
 * What a person who can do nothing in the platform sees at `/`: a hand-off to the
 * application of their default role, or the no-access page when there is nowhere to
 * hand them to (an application with no url in this environment counts as nowhere).
 * Only once a destination exists is the session switched to that destination's role,
 * so the application does not refuse them on arrival; if the switch is refused they
 * stay here on the no-access page. Someone holding a platform role on a different
 * active role keeps the no-access page too, which is where they are offered the switch.
 */
export function FrontDoor({ email }: { email: string }) {
  const { pathname } = useLocation();
  const { origin } = window.location;
  const heldRoles = useAppSelector(selectHeldRoles);
  const activeRole = useAppSelector(selectActiveRole);
  const atFrontDoor = pathname === APP_ROUTES.home && appRolesOf(heldRoles).length === 0;
  const { data, isPending, isError } = useMyApplications(atFrontDoor);
  const { mutate: switchRole, isError: switchFailed } = useSetActiveRole();
  const started = useRef(false);

  // The destination is worked out first, from hosts committed in contracts (so it is trusted
  // by construction). With none, nothing below runs: no role switch is attempted.
  const target = data
    ? frontDoorTarget(data, (applicationKey) => applicationUrlFor(applicationKey, origin))
    : null;

  useEffect(() => {
    if (!target || started.current) return;

    started.current = true;

    // A full load, not a route change: the destination is another origin.
    const arrive = () => window.location.assign(target.url);

    if (target.role === activeRole) {
      arrive();
      return;
    }

    switchRole(target.role, {
      onSuccess: () => {
        announceSession('role-switched');
        arrive();
      },
    });
  }, [target, activeRole, switchRole]);

  if (!atFrontDoor || isError || switchFailed) return <NoAccessPage email={email} />;

  if (isPending || target) return <HandingOver />;

  return <NoAccessPage email={email} />;
}

function HandingOver() {
  return (
    <div
      className="flex h-full items-center justify-center px-4"
      role="status"
      aria-label="Taking you to your application"
    >
      <Skeleton className="h-4 w-48" />
    </div>
  );
}
